import { getNotificationsUseCase } from 'useCases/notifications/getNotificationsUseCase';

import { AuthService } from 'services/Auth/AuthService';

import { Common } from 'utils';

import { alertEvent } from 'stores/Alert/AlertEvents';
import { authEvent } from 'stores/AuthStore/AuthEvents';

import { IAtuhEnum, IUser, IUserInfoResponse } from 'domains/auth/AuthDomain';

import { keycloak } from '../../keycloak';

const normalizeBase64 = (base64Url: string): string => {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (base64.length % 4)) % 4;
  return base64 + '='.repeat(padLength);
};

const getTokenRoles = (token?: string): string[] => {
  let parsed = keycloak?.tokenParsed as
    | {
        groups?: string[];
        realm_access?: { roles?: string[] };
        resource_access?: Record<string, { roles?: string[] }>;
      }
    | undefined;

  if (token) {
    try {
      const payload = token.split('.')[1];
      if (payload) {
        parsed = JSON.parse(atob(normalizeBase64(payload)));
      }
    } catch (err) {
      // Keep keycloak.tokenParsed as fallback when direct payload decoding fails.
    }
  }

  const groups = Array.isArray(parsed?.groups) ? parsed.groups : [];
  const realmRoles = Array.isArray(parsed?.realm_access?.roles) ? parsed.realm_access.roles : [];
  const resourceRoles = Object.values(parsed?.resource_access ?? {}).flatMap(resource =>
    Array.isArray(resource?.roles) ? resource.roles : [],
  );

  return [...new Set([...groups, ...realmRoles, ...resourceRoles].filter(Boolean))];
};

const extractUser = (response: IUserInfoResponse | IUser): IUser => {
  if ('data' in response) {
    return response.data;
  }

  return response;
};

const execute = async (params: { token: string }) => {
  try {
    const groups = getTokenRoles(params.token);
    const isAppRoute = window.location.pathname.startsWith('/app/');

    const userInfoResponse = await AuthService.getUserInfo(params.token);
    const user = extractUser(userInfoResponse as IUserInfoResponse | IUser);
    if (!user) {
      throw new Error('User info not found in auth response');
    }

    const isFranchiseAdmin = user.role === IAtuhEnum.FRANCHISE_ADMIN;
    if (isFranchiseAdmin && (user.accessStatus === 'Pending' || user.accessStatus === 'Revoked')) {
      const pending = {
        ...user,
        token: params.token,
        groups,
        permission: 4,
        isLogged: true,
        isAdmin: false,
        canManageFranchiseAccess: false,
      };
      authEvent(pending);
      Common.setSessionValue('logCookie', pending);
      if (window.location.pathname !== '/access-pending')
        window.location.replace('/access-pending');
      return;
    }
    if (isFranchiseAdmin && !user.franchiseId) throw new Error('Missing franchise scope');
    let permission = groups.length > 0 ? Common.getPermission(groups) : 2;
    if (user.role === IAtuhEnum.ADMIN) permission = 0;
    if (isFranchiseAdmin) permission = 3;
    const isAdmin = permission === 0 || isFranchiseAdmin;
    const userSector = permission === 0 ? IAtuhEnum.ADMIN_CODE : user.sector?.toString();
    const isNotAuthorized = !isAdmin && (!Number(user?.sector) || groups.length === 0);
    const searchParams = {
      name: permission === 0 ? IAtuhEnum.ADMIN : user?.name,
      sector: userSector,
    };
    const userAccessResponse = await AuthService.getUserAccess(searchParams, params.token);
    const userInfo = userAccessResponse?.data ?? userAccessResponse;

    if (isNotAuthorized) {
      await onError();
      return;
    }
    let role = isAdmin ? IAtuhEnum.ADMIN : userInfo?.role;
    if (isFranchiseAdmin) role = IAtuhEnum.FRANCHISE_ADMIN;
    const result = {
      ...user,
      ...userInfo,
      permission,
      role,
      franchiseId: user.franchiseId,
      accessStatus: user.accessStatus,
      canManageFranchiseAccess: permission === 0 && user.canManageFranchiseAccess === true,
      token: params.token,
      groups,
      isLogged: true,
      isAdmin,
    };
    authEvent(result);
    Common.setSessionValue('logCookie', result);
    const franchisePages = [
      '/app/request-management',
      '/app/product-management',
      '/app/medicine-management',
    ];
    if (!isAppRoute || (isFranchiseAdmin && !franchisePages.includes(window.location.pathname))) {
      window?.location?.replace(isFranchiseAdmin ? '/app/request-management' : '/app/new-request');
    }
    getNotificationsUseCase.execute();
  } catch (err) {
    await onError();
  }
};

const onError = async () => {
  alertEvent({ open: true, type: 'error', text: 'Usuário sem permissão.' });
  await Common.sleep(1500);
  Common.setSessionValue('logCookie', null);
  window?.location?.replace('/401');
};

export const loginSSOUseCase = { execute };
